/**
 * Assistant-side transcript line for an `ask_user` (HITL) turn.
 *
 * The interactive question lives in the composer-attached card
 * (`ComposerAskUserPanel`), so the assistant message that triggers it would
 * otherwise render as a blank bubble. This renders a short, permanent summary —
 * "Asked a question" + the question title(s) — so the thread reads coherently
 * both while pending and after the question is answered/dismissed.
 *
 * Sourced from the persisted `askUserRef.snapshot.questions` on reload, or the
 * live synthesized `__ask_user` part during a run (see `agent-assistant-message`).
 */

import { HelpCircle } from 'lucide-react';
import type { AskUserQuestion } from '@/types/inbox';

export function AskUserSummary({ questions }: { questions: AskUserQuestion[] }) {
  if (!questions || questions.length === 0) return null;

  const isReview = questions.every((q) => q.inputType === 'review');
  const label = isReview
    ? 'Requested a review'
    : questions.length > 1
      ? 'Asked a few questions'
      : 'Asked a question';

  return (
    <div className="my-1 flex items-start gap-2 text-sm">
      <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary/70" />
      <div className="min-w-0">
        <span className="text-muted-foreground">{label}</span>
        {!isReview && (
          <ul className="mt-0.5 space-y-0.5">
            {questions.map((q, i) => (
              <li key={q.questionId || i} className="text-foreground/90">
                {q.header ? (
                  <span className="text-muted-foreground">{q.header}: </span>
                ) : null}
                {q.question}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
